import { useEffect, useState } from "react";
import { MCQActivity } from "./Activities";

/**
 * FinalQuiz — runs 5-10 MCQs sequentially, then shows score + pass/fail.
 */
export default function FinalQuiz({ questions, passingScore = 60, onFinished }) {
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState([]); // bool[]
  const [done, setDone] = useState(false);

  const current = questions[idx];
  const total = questions.length;
  const finalCorrect = answers.filter(Boolean).length;
  const finalScore = total ? Math.round((finalCorrect / total) * 100) : 0;
  const finalPassed = finalScore >= passingScore;

  // Fire onFinished exactly once when quiz completes, with the fresh score
  useEffect(() => {
    if (done) onFinished && onFinished({ score: finalScore });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [done]);

  const onAnswered = (ok) => {
    setAnswers((prev) => [...prev, ok]);
  };

  const next = () => {
    if (idx + 1 < total) {
      setIdx(idx + 1);
    } else {
      setDone(true);
    }
  };

  const retake = () => {
    setIdx(0);
    setAnswers([]);
    setDone(false);
  };

  if (done) {
    return (
      <div className="quiz-result" data-testid="quiz-result">
        <div
          className="ring"
          style={{
            "--p": finalScore,
            margin: "0 auto 14px",
            display: "grid",
            placeItems: "center",
            width: 110,
            height: 110,
            borderRadius: "50%",
            background: `conic-gradient(${finalPassed ? "var(--emerald)" : "var(--rose)"} ${finalScore}%, rgba(255,255,255,0.08) 0)`,
            position: "relative",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 7,
              background: "var(--bg-1)",
              borderRadius: "50%",
            }}
          />
          <span style={{ position: "relative", fontSize: 26, fontWeight: 900, color: finalPassed ? "var(--emerald)" : "var(--rose)" }}>
            {finalScore}
          </span>
        </div>
        <h3 style={{ textAlign: "center", margin: "6px 0", fontFamily: "'Amiri', serif", fontSize: 26 }}>
          {finalPassed ? "أحسنت! اجتزت الاختبار 🎉" : "لم تجتز هذه المرّة"}
        </h3>
        <p className="dim" style={{ textAlign: "center", lineHeight: 1.85 }}>
          {finalPassed
            ? `أجبت بشكل صحيح على ${answers.filter(Boolean).length} من أصل ${total} أسئلة.`
            : `أجبت بشكل صحيح على ${answers.filter(Boolean).length} من ${total}. الحدّ الأدنى للنجاح: ${passingScore}%.`}
        </p>
        <div className="row" style={{ justifyContent: "center", marginTop: 12 }}>
          <button className="btn btn-outline" onClick={retake} data-testid="quiz-retake-btn">
            إعادة الاختبار
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="final-quiz">
      <div className="quiz-progress" data-testid="quiz-progress">
        <span>السؤال {idx + 1} من {total}</span>
        <div className="progress-track" style={{ flex: 1, height: 6 }}>
          <div className="progress-fill" style={{ width: `${((idx) / total) * 100}%` }} />
        </div>
      </div>
      <MCQActivity
        key={idx}
        question={current.question}
        options={current.options}
        correctIndex={current.correct_index}
        explanation={current.explanation}
        onAnswer={onAnswered}
      />
      {answers.length > idx && (
        <div className="row" style={{ justifyContent: "flex-end", marginTop: 10 }}>
          <button className="btn btn-primary" onClick={next} data-testid="quiz-next-btn">
            {idx + 1 < total ? "السؤال التالي ←" : "إنهاء الاختبار"}
          </button>
        </div>
      )}
    </div>
  );
}
